import {useCallback} from 'react';
import {Platform} from 'react-native';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import * as DocumentPicker from 'expo-document-picker';
import {File, Paths, Directory} from 'expo-file-system';
import axios from 'axios';
import Alert from '@/lib/Alert';
import {ALLOWED_MEDIA_TYPES} from '@/lib/allowed-media-types';
import {generatePreviewImage} from '@/lib/preview-image';
import {appDataKeys} from '@/data/app-data-keys';
import {useUploadState} from '@/contexts/UploadContext';
import {
    createMediaInSharedFolder,
    getConfidantSignedUrl,
    getConfidantUploadId,
    getConfidantMultipartUrl,
    finalizeConfidantUpload,
    finalizeConfidantMultipartUpload,
} from '@/data/shared-folder-api';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MiB

// Plain axios instance — no baseURL or auth interceptors so signed S3 URLs work as-is
const s3Client = axios.create();

function getExtension(name: string): string {
    const dotIndex = name.lastIndexOf('.');
    return dotIndex >= 0 ? name.substring(dotIndex + 1) : '';
}

export function useSharedFolderUpload(orgId: string, folderId: string | undefined) {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {setUploadState, resetUploadState} = useUploadState();

    const pickAndUpload = useCallback(async () => {
        if (!folderId) return;

        const result = await DocumentPicker.getDocumentAsync({
            copyToCacheDirectory: true,
            type: ALLOWED_MEDIA_TYPES,
        });
        if (result.canceled || !result.assets?.length) return;

        const asset = result.assets[0];
        const name = asset.name;
        const size = asset.size ?? 0;
        const mimeType = asset.mimeType ?? 'application/octet-stream';
        const extension = getExtension(name);

        setUploadState({isUploading: true, progress: 0, fileName: name});

        try {
            // Generate preview image for videos and images (mobile only)
            const previewImageUri = await generatePreviewImage(asset.uri, mimeType);

            // Create media record on the backend
            const mediaRecord = await createMediaInSharedFolder({
                name: name.replace(`.${extension}`, ''),
                organization: orgId,
                originalContentType: mimeType,
                originalSuffix: extension,
                folder: folderId,
            }, previewImageUri);
            const mediaId = mediaRecord.id;

            // Free blob URL memory on web now that the preview has been uploaded
            if (previewImageUri && Platform.OS === 'web') {
                URL.revokeObjectURL(previewImageUri);
            }

            const isMultipart = size >= CHUNK_SIZE;

            if (!isMultipart) {
                // --- Single-part upload ---
                const signedUrl = await getConfidantSignedUrl(mediaId);

                if (Platform.OS === 'web') {
                    const response = await fetch(asset.uri);
                    const blob = await response.blob();
                    await s3Client.put(signedUrl, blob, {
                        onUploadProgress: (event) => {
                            if (event.total) setUploadState({progress: event.loaded / event.total});
                        },
                    });
                } else {
                    // On mobile, fetch supports file URIs directly
                    setUploadState({progress: -1}); // indeterminate
                    await fetch(signedUrl, {method: 'PUT', body: {uri: asset.uri, type: mimeType, name} as any});
                }

                await finalizeConfidantUpload(mediaId);
            } else {
                // --- Multipart upload ---
                const uploadId = await getConfidantUploadId(mediaId);
                const totalChunks = Math.ceil(size / CHUNK_SIZE);
                const parts: {PartNumber: number; ETag: string}[] = [];

                if (Platform.OS === 'web') {
                    // On web: fetch file as blob, slice into chunks
                    const response = await fetch(asset.uri);
                    const fileBlob = await response.blob();

                    for (let i = 0; i < totalChunks; i++) {
                        const partNumber = i + 1;
                        const start = i * CHUNK_SIZE;
                        const end = Math.min(start + CHUNK_SIZE, size);
                        const chunk = fileBlob.slice(start, end);

                        const partUrl = await getConfidantMultipartUrl(mediaId, uploadId, partNumber);
                        const partResponse = await s3Client.put(partUrl, chunk, {
                            onUploadProgress: (event) => {
                                if (event.total) setUploadState({progress: (i + event.loaded / event.total) / totalChunks});
                            },
                        });
                        const etag = (partResponse.headers['etag'] as string) || '';
                        parts.push({PartNumber: partNumber, ETag: etag.replace(/"/g, '')});
                        setUploadState({progress: (i + 1) / totalChunks});
                    }
                } else {
                    // On mobile: read file using expo-file-system, slice bytes
                    const cacheDir = new Directory(Paths.cache, 'och-upload-chunks');
                    if (!cacheDir.exists) {
                        cacheDir.create();
                    }

                    // Copy asset to a known file path so we can read it
                    const sourceFile = new File(asset.uri);
                    const fileBytes = await sourceFile.bytes();

                    for (let i = 0; i < totalChunks; i++) {
                        const partNumber = i + 1;
                        const start = i * CHUNK_SIZE;
                        const end = Math.min(start + CHUNK_SIZE, size);
                        const chunkBytes = fileBytes.slice(start, end);

                        // Write chunk to a temp file for upload
                        const chunkFile = new File(cacheDir, `chunk_${partNumber}`);
                        chunkFile.write(chunkBytes);

                        const partUrl = await getConfidantMultipartUrl(mediaId, uploadId, partNumber);
                        const res = await fetch(partUrl, {
                            method: 'PUT',
                            body: {uri: chunkFile.uri, type: mimeType, name: `chunk_${partNumber}`} as any,
                        });

                        const etag = res.headers.get('etag') || '';
                        parts.push({PartNumber: partNumber, ETag: etag.replace(/"/g, '')});

                        // Clean up chunk file
                        if (chunkFile.exists) {
                            chunkFile.delete();
                        }

                        setUploadState({progress: (i + 1) / totalChunks});
                    }
                }

                await finalizeConfidantMultipartUpload(mediaId, uploadId, parts);
            }

            setUploadState({isUploading: false, progress: 1});
            // Refresh media list
            await queryClient.invalidateQueries({queryKey: appDataKeys.all});

            // Keep progress/fileName alive so the toast can display them during fade-out
            setTimeout(() => resetUploadState(), 3000);
        } catch (error: any) {
            console.error('Upload failed:', error);
            Alert.alert(
                t('media.sharedFolders.uploadError'),
                t('media.sharedFolders.uploadErrorMessage'),
            );
            resetUploadState();
        }
    }, [folderId, orgId, queryClient, t, setUploadState, resetUploadState]);

    return {pickAndUpload};
}
